import { html, svg, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { getIcon } from './icon-registry';
import { devWarn } from '../luna-common';

const ICON_SIZES = [14, 18, 20, 22, 24, 28] as const;

@customElement('luna-icon')
export class LunaIcon extends LitPage {
  private _name = '';

  @property({ attribute: 'name' })
  get name(): string {
    return this._name;
  }

  set name(value: string | null) {
    const next = String(value ?? '');
    if (next !== '' && !getIcon(next)) {
      devWarn(`luna-icon: unknown icon name "${next}"`);
      this._name = '';
      return;
    }
    this._name = next;
  }

  private _size = 24;

  @property({ type: Number })
  get size(): number {
    return this._size;
  }

  set size(value: number | null) {
    const next = Number(value);
    if (!Number.isFinite(next) || !(ICON_SIZES as readonly number[]).includes(next)) {
      devWarn(`luna-icon: invalid size "${value}" — falling back to 24`);
      this._size = 24;
      return;
    }
    this._size = next;
  }

  @property({ attribute: 'label' }) label = '';

  content(): TemplateResult<1> {
    const glyph = getIcon(this._name);
    if (!glyph) {
      return html``;
    }

    const decorative = this.label === '';
    const isStroke = glyph.paint === 'stroke';

    // Paint resolves through `currentColor`, inheriting the host/container
    // `color` (components set it — e.g. on-bar white, per-puzzle Play label).
    // There is no declared default here on purpose: a `color` on the host or
    // `<svg>` would override any ancestor colour (declared beats inherited),
    // breaking the component contract. The app's inherited text colour equals
    // `--cds-icon-primary`, so a standalone icon is already correct.

    return svg`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width=${this._size}
        height=${this._size}
        viewBox=${glyph.viewBox}
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden=${decorative ? 'true' : 'false'}
        role=${decorative ? undefined : 'img'}
        aria-label=${decorative ? undefined : this.label}
      >
        <path
          d=${glyph.pathData}
          fill=${isStroke ? 'none' : 'currentColor'}
          stroke=${isStroke ? 'currentColor' : 'none'}
        ></path>
      </svg>
    ` as unknown as TemplateResult<1>;
  }
}
